import {
    Boxes, Layers, LayoutDashboard, GitBranch, Database, Cloud, Workflow, Palette, ServerCog,
    Compass, Pencil, Hammer, BrainCircuit, CloudCog, Rocket, Sparkles,
} from "lucide-react";
import PageMeta from "../components/layout/PageMeta";
import PageHero from "../components/sections/PageHero";
import FinalCTA from "../components/sections/FinalCTA";

const serviceClusters = [
    {
        name: "Build",
        description: "From-scratch design and full-stack development of production systems.",
        items: [
            { icon: <Boxes className="w-5 h-5" />, title: "SaaS Product Development", copy: "Multi-tenant SaaS products built for scale, monetisation and rapid iteration." },
            { icon: <Layers className="w-5 h-5" />, title: "Custom Web Applications", copy: "Tailored applications that map cleanly to your business processes and audiences." },
            { icon: <LayoutDashboard className="w-5 h-5" />, title: "Enterprise Portals", copy: "Employee, partner and customer portals that consolidate fragmented internal systems." },
        ]
    },
    {
        name: "Modernize",
        description: "Inject intelligence, modern interfaces, and scalable data operations.",
        items: [
            { icon: <GitBranch className="w-5 h-5" />, title: "Vendor Management Systems", copy: "VMS platforms purpose-built for staffing, procurement and partner ecosystems." },
            { icon: <Sparkles className="w-5 h-5" />, title: "AI Dashboards", copy: "Decision-support dashboards with embedded models, explainability and alerts." },
            { icon: <Database className="w-5 h-5" />, title: "Data Platforms", copy: "Lakehouse, warehouse and streaming foundations engineered for AI-ready data." },
        ]
    },
    {
        name: "Operate",
        description: "Integrations, design systems, and cloud platform engineering.",
        items: [
            { icon: <Workflow className="w-5 h-5" />, title: "API Integrations", copy: "Resilient API and event integrations across SaaS, ERP, CRM and internal systems." },
            { icon: <Palette className="w-5 h-5" />, title: "UX / UI Design", copy: "Research-led product design — from low-fidelity wireframes to production design systems." },
            { icon: <ServerCog className="w-5 h-5" />, title: "DevOps & Deployment", copy: "Cloud-native CI/CD, infrastructure as code, observability and platform reliability." },
        ]
    }
];

const phases = [
    { icon: <Compass className="w-4 h-4" />, title: "Product discovery", copy: "Stakeholder mapping, problem framing, success metrics and tech feasibility.", output: "architecture brief, delivery plan" },
    { icon: <Pencil className="w-4 h-4" />, title: "UX & prototyping", copy: "Information architecture, design systems and clickable prototypes for validation.", output: "clickable prototype" },
    { icon: <Hammer className="w-4 h-4" />, title: "Full-stack development", copy: "Modern frontend, robust APIs and engineered data flows across your stack.", output: "working product increments" },
    { icon: <BrainCircuit className="w-4 h-4" />, title: "AI & data integration", copy: "Embed models, retrieval, agents and analytics where they create the most value.", output: "embedded models, dashboards, workflows" },
    { icon: <CloudCog className="w-4 h-4" />, title: "Cloud deployment", copy: "Hardened deployments on AWS, Azure or GCP with security and compliance reviews.", output: "secure deployment" },
    { icon: <Rocket className="w-4 h-4" />, title: "Support & scaling", copy: "SLOs, monitoring, optimisation and feature delivery for the long haul.", output: "monitoring and iteration" },
];

export default function ProductEngineering() {
    return (
        <div className="pqx-page-enter">
            <PageMeta
                title="Product Engineering"
                description="From idea to scalable product — SaaS, enterprise portals, AI dashboards and data platforms built with modern engineering and cloud foundations."
            />

            <PageHero
                eyebrow="Product Engineering"
                title="Senior squads."
                titleAccent="Production-grade software."
                description="Perceptiqx product squads design, build and operate the platforms enterprises depend on — with AI woven into the product, not bolted on the side. Cloud-native, observable and owned by the team we help you hire."
                primaryCta={{ to: "/contact", label: "Scope a product build" }}
                secondaryCta={{ to: "/ai-solutions", label: "Add AI capabilities" }}
                bullets={["SaaS & enterprise portals", "Data & AI platforms", "Cloud-native delivery", "Production-grade UX"]}
            />

            {/* Services */}
            <section className="pqx-section bg-[#FAFAF8]" data-testid="pe-services">
                <div className="pqx-container">
                    <div className="max-w-3xl">
                        <span className="pqx-eyebrow">Services</span>
                        <h2 className="pqx-display mt-5 text-3xl md:text-5xl text-[#303030]">
                            Full-stack engineering clusters.
                        </h2>
                    </div>

                    <div className="mt-14 space-y-12">
                        {serviceClusters.map((cluster) => (
                            <div key={cluster.name} className="border-b border-[#303030]/10 pb-10 last:border-0 last:pb-0">
                                <div className="grid lg:grid-cols-[1.1fr_2.9fr] gap-6 lg:gap-10">
                                    <div>
                                        <h3 className="pqx-display text-2xl text-[#303030] font-bold">
                                            {cluster.name}
                                        </h3>
                                        <p className="mt-2 text-sm text-[#303030]/60 leading-relaxed">
                                            {cluster.description}
                                        </p>
                                    </div>
                                    <div className="grid sm:grid-cols-3 gap-4">
                                        {cluster.items.map((item) => (
                                            <div key={item.title} className="pqx-card-elevated !p-6 group flex flex-col justify-between bg-white border border-[#303030]/5 rounded-2xl">
                                                <div>
                                                    <span className="w-10 h-10 grid place-items-center rounded-xl bg-[#303030]/5 text-[#303030] group-hover:bg-[#FF4040] group-hover:text-white transition-colors">
                                                        {item.icon}
                                                    </span>
                                                    <h4 className="pqx-display text-[15px] text-[#303030] mt-4 font-bold leading-tight">
                                                        {item.title}
                                                    </h4>
                                                </div>
                                                <p className="mt-3 text-[12.5px] text-[#303030]/70 leading-relaxed">
                                                    {item.copy}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Phases Timeline */}
            <section
                className="pqx-section bg-[#191919] text-[#FAFAF8] relative overflow-hidden"
                data-testid="pe-phases"
            >
                <div className="absolute inset-0 pqx-grid-bg-dark opacity-40" />
                <div className="pqx-container relative z-10">
                    <div className="max-w-3xl">
                        <span className="pqx-eyebrow !text-[#FF4040]">Delivery model</span>
                        <h2 className="pqx-display mt-5 text-3xl md:text-5xl">
                            A sequenced squad operating rhythm.
                        </h2>
                    </div>

                    <div className="mt-14 relative border-l border-[#FF4040]/30 ml-4 md:ml-6 space-y-12">
                        {phases.map((p, i) => (
                            <div
                                key={p.title}
                                data-testid={`pe-phase-${i}`}
                                className="relative pl-8 md:pl-10 group"
                            >
                                {/* Dot indicator */}
                                <div className="absolute left-[-9px] top-1.5 w-[17px] h-[17px] rounded-full bg-[#191919] border-2 border-[#FF4040] group-hover:bg-[#FF4040] transition-colors duration-300" />
                                
                                <div className="grid md:grid-cols-[1.5fr_1fr] gap-6 items-start pqx-glass-dark rounded-2xl p-6 border border-white/8 hover:border-[#FF4040]/30 transition-all">
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <span className="font-mono text-sm text-[#FF4040] font-bold">
                                                Stage 0{i + 1}
                                            </span>
                                            <span className="w-6 h-6 grid place-items-center rounded bg-white/8 text-white/70">
                                                {p.icon}
                                            </span>
                                            <h3 className="pqx-display text-lg text-white">{p.title}</h3>
                                        </div>
                                        <p className="mt-3 text-sm text-white/65 leading-relaxed">
                                            {p.copy}
                                        </p>
                                    </div>
                                    <div className="md:border-l md:border-white/10 md:pl-6 pt-4 md:pt-0 flex flex-col justify-between h-full">
                                        <p className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-semibold">Key Deliverable</p>
                                        <p className="mt-2 text-sm text-[#FF4040] font-semibold flex-1">
                                            {p.output}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <FinalCTA
                title="Have a product to ship?"
                description="Bring us your roadmap. We’ll turn it into a delivery plan."
                primary={{ to: "/contact", label: "Scope a product build" }}
                secondary={{ to: "/products", label: "See accelerators" }}
            />
        </div>
    );
}
